'use client'

import { useEffect } from 'react'

import {
  isChunkLoadError,
  olvidarIntentosDeRecuperacion,
  recoverFromChunkError,
} from '@/common/helpers/chunkErrorRecovery'
import { desactivarRecuperacionInicial } from '@/common/helpers/recuperacionInicialDeChunks'

// Si la app lleva este tiempo andando sin fallar, se considera recuperada y el presupuesto de
// recargas se devuelve, para que un incidente futuro no arranque con los intentos gastados.
const MS_PARA_DARLA_POR_SANA = 15_000

/**
 * Captura los fallos de carga de chunk que NO pasan por el error boundary.
 *
 * Son dos caminos distintos:
 *
 * 1. **Promesas rechazadas** — un import dinámico o un prefetch que falla fuera del render.
 * 2. **Fallos de recurso** — el navegador se niega a ejecutar el `<script>` porque el 404 vino con
 *    `content-type: text/plain`. Eso NO rechaza ninguna promesa ni lanza en React: dispara un evento
 *    `error` en el elemento. Y los errores de recurso **no burbujean**, así que el listener tiene que
 *    ir en fase de captura (`true`) o no se ve nunca. Este camino era el que faltaba.
 */
const ChunkErrorRecovery: React.FC = () => {
  useEffect(() => {
    // El script en línea del <head> cubría el arranque; desde aquí lo cubre este componente.
    desactivarRecuperacionInicial()

    const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
      if (isChunkLoadError(event.reason) && recoverFromChunkError()) {
        event.preventDefault()
      }
    }

    const onResourceError = (event: Event): void => {
      const el = event.target

      // Un error de JS también llega como evento `error` en window, con target === window.
      // Ese ya lo cubre el boundary; acá solo interesan los `<script>` y `<link>` del build.
      const url =
        el instanceof HTMLScriptElement ? el.src : el instanceof HTMLLinkElement ? el.href : ''

      if (url.includes('/_next/static/')) {
        recoverFromChunkError()
      }
    }

    const olvidar = window.setTimeout(olvidarIntentosDeRecuperacion, MS_PARA_DARLA_POR_SANA)

    window.addEventListener('unhandledrejection', onUnhandledRejection)
    window.addEventListener('error', onResourceError, true)

    return () => {
      window.clearTimeout(olvidar)
      window.removeEventListener('unhandledrejection', onUnhandledRejection)
      window.removeEventListener('error', onResourceError, true)
    }
  }, [])

  return null
}

export default ChunkErrorRecovery
